import { useState } from 'react'
import { Check, Plus } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Banner, Field, Row, Sheet, useToast } from '../ui'
import { KindIcon } from './KindIcon'
import { useAddItemForEntry, useAddListForEntry, useRemoveItem } from '../lib/mutations'
import { useLists, useListItems } from '../lib/queries'
import { attempt, useWriteError } from '../lib/useAction'
import { rowTitle } from '../lib/rowMeta'
import type { FeedRow, List, ListItem, ListKind } from '../lib/types'
import { KindPicker } from './KindPicker'
import { EVENT_KIND_OPTIONS, listsForEvent } from '../lib/listKinds'

/**
 * L8: "Add to list", reached from the row's long-press menu (`EventActions`)
 * and, since a2-23, the event sheet's own action row — one sheet deep rather
 * than three. The lists an event fits (`listsForEvent`) as `Row`s, ticked
 * where it already holds this entry. Tap toggles: `addItemForEntry` where it
 * does not, `removeItem` where it does. Rule 1 lives here rather than in the
 * mutation itself — this is the only caller, and it never fires the add for a
 * (list, entry) pair `list_items` already carries.
 */
export function AddToListSheet({ entry, open, onClose }: {
  entry: FeedRow
  open: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<ListKind>('task')

  const listsQ = useLists()
  const itemsQ = useListItems()
  const addM = useAddItemForEntry()
  const removeM = useRemoveItem()
  const addListM = useAddListForEntry()
  const error = useWriteError(addM, removeM, addListM)

  // a2-23: only the lists an event fits, in the Lists index's order.
  const lists = listsForEvent(listsQ.data ?? [])
  const items = itemsQ.data ?? []
  const itemFor = (listId: string) => items.find(i => i.list_id === listId && i.entry_id === entry.id) ?? null

  /** Read fresh at click time rather than closed over — by the time Undo is
   *  tapped the optimistic item from `toggle`'s own `addM.mutate` is already
   *  in the cache under a temp id, and this is what finds it. */
  function currentItemFor(listId: string): ListItem | null {
    const cached = qc.getQueryData<ListItem[]>(['list_items']) ?? []
    return cached.find(i => i.list_id === listId && i.entry_id === entry.id) ?? null
  }

  function toggle(list: List) {
    const existing = itemFor(list.id)
    if (existing) {
      removeM.mutate(existing.id)
      toast({
        text: `Removed from ${list.name}`,
        action: { label: 'Undo', onClick: () => addM.mutate({ listId: list.id, entryId: entry.id, body: rowTitle(entry) }) },
      })
      return
    }
    addM.mutate({ listId: list.id, entryId: entry.id, body: rowTitle(entry) })
    toast({
      text: `Added to ${list.name}`,
      action: {
        label: 'Undo',
        onClick: () => { const it = currentItemFor(list.id); if (it) removeM.mutate(it.id) },
      },
    })
  }

  async function createAndAdd() {
    const trimmed = name.trim()
    if (!trimmed) return
    const ok = await attempt(addListM.mutateAsync({ name: trimmed, kind, entryId: entry.id, body: rowTitle(entry) }))
    if (ok) { setCreating(false); setName(''); setKind('task') }
  }

  return (
    <Sheet open={open} onOpenChange={o => { if (!o) onClose() }} title="Add to list" closeButton>
      <div className="ev-acts">
        {error && <Banner tone="bad">{error}</Banner>}
        {lists.map(l => (
          <Row
            key={l.id}
            className="u-row--list"
            leading={<span className="lists-tile"><KindIcon kind={l.kind} /></span>}
            title={l.name}
            meta={null}
            trailing={itemFor(l.id) ? <Check size={18} aria-hidden="true" /> : null}
            onPress={() => toggle(l)}
          />
        ))}
        <Row
          className="u-row--list"
          leading={<span className="lists-tile"><Plus size={18} aria-hidden="true" /></span>}
          title="New list"
          meta={null}
          onPress={() => setCreating(true)}
        />
        {creating && (
          <div className="ev-acts__datepick">
            <Field label="Name">
              <Field.Input value={name} autoFocus placeholder="Books" onChange={e => setName(e.target.value)} />
            </Field>
            <KindPicker value={kind} onChange={setKind} options={EVENT_KIND_OPTIONS} />
            <Row
              className="u-row--list"
              leading={<Plus size={20} aria-hidden="true" />}
              title="Create"
              meta={null}
              disabled={!name.trim() || addListM.isPending}
              onPress={() => void createAndAdd()}
            />
          </div>
        )}
      </div>
    </Sheet>
  )
}
